import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { showNotification } from '@mantine/notifications';

import { UserContext } from '~/components/providers/UserProvider';
import { updateContestInviteStatus } from '~/services/commissioner/commissioner-service.service.api';
import type { UpdateContestInviteStatusPayload } from '~/services/commissioner/commissioner-service.service.api';

import { ContestMutationKeys, ContestQueryKeys } from '../constants/query';

type UseUpdateContestInviteStatusArgs = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

const useUpdateContestInviteStatus = ({
  onSuccess,
  onError,
}: UseUpdateContestInviteStatusArgs = {}) => {
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ContestMutationKeys.UPDATE_CONTEST_INVITE({ userId: user?.id || '' }),
    mutationFn: ({
      contestInviteId,
      payload,
    }: {
      contestInviteId: string;
      payload: UpdateContestInviteStatusPayload;
    }) => updateContestInviteStatus(contestInviteId, payload),
    onSuccess: (data, variables) => {
      const { payload } = variables;

      showNotification({
        title: 'Success',
        message: `Contest invite ${payload.status.toLowerCase()}!`,
        color: 'green',
        autoClose: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: ContestQueryKeys.CONTEST_INVITES_RECEIVED({ userId: user?.id || '' }),
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      showNotification({
        title: 'Error',
        message: 'Failed to update contest invite status',
        color: 'red',
        autoClose: 3000,
      });
      onError?.(error);
    },
  });
};

export default useUpdateContestInviteStatus;
